'use client';

import { StorageService } from '@nextgen-web-framework/core';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'i18n/routing';

export const SESSION_STORAGE_INTERNAL_NAV_KEY = 'amshop_has_navigated_internally';

export interface InternalNavigationTrackerContextType {
  hasNavigatedInternally?: boolean;
}

export interface InternalNavigationTrackerProviderProps {
  readonly children: ReactNode;
}

const InternalNavigationTrackerProviderContext = createContext<InternalNavigationTrackerContextType>({});

const InternalNavigationTrackerProvider = ({ children }: InternalNavigationTrackerProviderProps) => {
  const pathname = usePathname();
  const isFirstMount = useRef(true);
  const sessionstorage = StorageService.getSessionStorage();

  const [hasNavigatedInternally, setHasNavigatedInternally] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionstorage.retrieve(SESSION_STORAGE_INTERNAL_NAV_KEY) === 'true';
    }
    return false;
  });

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else {
      sessionstorage.store(SESSION_STORAGE_INTERNAL_NAV_KEY, 'true');
      setHasNavigatedInternally(true);
    }
  }, [pathname]);

  const value = useMemo(
    () => ({
      hasNavigatedInternally,
    }),
    [hasNavigatedInternally],
  );

  return (
    <InternalNavigationTrackerProviderContext.Provider value={value}>
      {children}
    </InternalNavigationTrackerProviderContext.Provider>
  );
};

const useInternalNavigationTracker = () => {
  const internalNavigationTrackerContext = useContext(InternalNavigationTrackerProviderContext);
  return internalNavigationTrackerContext;
};

export { useInternalNavigationTracker };

export default InternalNavigationTrackerProvider;
